<template>
  <div class="step-card">
    <div class="step-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">What subject(s) do you teach?</h2>
        <p class="step-subtitle">Select all that apply</p>
      </div>

      <!-- Subject Selection Options -->
      <div class="options-container">
        <button
          v-for="option in options"
          :key="option.id"
          class="option-button"
          :class="{ selected: isSelected(option.id) }"
          :aria-pressed="isSelected(option.id)"
          @click="toggleSelection(option.id)"
        >
          {{ option.label }}
        </button>
      </div>

      <!-- Button Container -->
      <div class="button-container">
        <ActionButton @click="handleBack" variant="outline" class="back-button"
          >Back</ActionButton
        >
        <div class="right-buttons">
          <ActionButton @click="handleSkip" variant="text" class="skip-button"
            >Skip</ActionButton
          >
          <ActionButton
            :disabled="selectedItems.length === 0"
            @click="handleNext"
            class="next-button"
            >Next
          </ActionButton>
        </div>
      </div>
    </div>
  </div>
  <ActionButton @click="handleSkip" variant="text" class="skip-button-mobile"
    >Skip</ActionButton
  >
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps, defineEmits, watch } from "vue";
import ActionButton from "../common/ActionButton.vue";

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["next", "back", "skip", "update:modelValue"]);

const selectedItems = ref([...props.modelValue]);

// Watch for prop changes (when navigating back)
watch(
  () => props.modelValue,
  (newValue) => {
    // Only update if the value actually changed to prevent loops
    const currentIds = [...selectedItems.value].sort().join(",");
    const newIds = (newValue ? [...newValue] : []).sort().join(",");
    if (currentIds !== newIds) {
      selectedItems.value = newValue ? [...newValue] : [];
    }
  },
  { immediate: true }
);

const isSelected = (id) => {
  return selectedItems.value.includes(id);
};

const toggleSelection = (id) => {
  const index = selectedItems.value.indexOf(id);
  if (index === -1) {
    selectedItems.value.push(id);
  } else {
    selectedItems.value.splice(index, 1);
  }
  // Emit update only when user makes a change
  emit("update:modelValue", [...selectedItems.value]);
};

const handleNext = () => {
  if (selectedItems.value.length > 0) {
    emit("next", { selections: selectedItems.value });
  }
};

const handleBack = () => {
  emit("back");
};

const handleSkip = () => {
  emit("skip");
};
</script>

<style scoped>
.step-card {
  background-color: white;
  border: 1px solid #0478ce;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 32px;
  border-radius: 16px;
  width: 100%;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-end;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  color: #3b4648;
}

.step-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xxl);
  line-height: 36px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

.step-subtitle {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  margin: 0;
  width: 100%;
}

/* Options Container */
.options-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
  justify-content: start;
}

.option-button {
  padding: 12px 24px;
  border: 1px solid #d8dae5;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: white;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: var(--neutral-900);
  text-align: center;
  box-sizing: border-box;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.option-button:hover {
  border-color: #0478ce;
  background-color: #f2f3fb;
}

.option-button.selected {
  background-color: var(--primary-blue);
  border-color: var(--primary-blue);
  color: white;
}

.option-button.selected:hover {
  background-color: #002a4a;
  border-color: #002a4a;
}

/* Button Container */
.button-container {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.right-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.back-button {
  background-color: white;
  border: 1px solid #d8dae5;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  min-width: 100px;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #3b4648;
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.back-button:hover {
  background-color: #f2f3fb;
  border-color: #0478ce;
}

.skip-button {
  width: 100px;
  background-color: #f2f3fb;
  border: none;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--primary-blue);
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.skip-button:hover {
  color: #3b4648;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

.next-button {
  background-color: #dfe2e7;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  height: 40px;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 8px;
  width: 100px;
  border: none;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  color: var(--neutral-700);
  white-space: nowrap;
  letter-spacing: 0.014px;
  transition: all 0.3s ease;
}

.next-button:not(.disabled) {
  background-color: var(--primary-blue);
  color: white;
  cursor: pointer;
}

.next-button:not(.disabled):hover {
  background-color: #002a4a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 61, 106, 0.3);
}

.next-button.disabled {
  cursor: not-allowed;
}

.skip-button-mobile {
  display: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .step-card {
    min-height: auto;
  }

  .options-container {
    justify-content: flex-start;
  }

  .button-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .right-buttons {
    width: 100%;
  }

  .back-button {
    margin-bottom: 0px;
  }

  .skip-button {
    display: none;
  }

  .skip-button-mobile {
    margin-top: 10px;
    display: block;
  }

  .back-button,
  .skip-button,
  .next-button {
    flex: 1;
  }
}
</style>
